var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var RestaumaticClient_1;
var _a;
import "reflect-metadata";
import { 
// NOTICE This class is allowed to use global container
// eslint-disable-next-line no-restricted-imports
container, singleton, } from "tsyringe";
import { GlobalConfig, MenuConsumer } from "../config";
import { makeImmutable } from "../core";
import { ApartmentInfoFactory, PCartIntegration, PerRestaurantContainerFactory, PMobileApps, POrderSummaryFactory, PRestaurantSelectionFactory, StreetAddressFactory, } from "../factory";
import { LocalizationProvider } from "../locale";
import { ApartmentInfoToken, BackendDataService, SalesChannel, StreetAddress, } from "../ordering";
import { MarketingConsentWithdrawalController, PCart, PCheckout, PlaceOrderCallback, PRestaurant, } from "../presentation";
import { ApplePayServiceProvider, IQrWaiter, MarketingConsentWithdrawalService, MenuService, RestaumaticRPCService, } from "../services";
import { StorageManagerFactory } from "../storage";
import { IStorage } from "../storageBase";
let RestaumaticClient = RestaumaticClient_1 = class RestaumaticClient {
    constructor(storageManagerFactory) {
        this.storageManagerFactory = storageManagerFactory;
        makeImmutable(this);
    }
    /**
     * Registers the primitives every entry point needs: config, RPC, i18n and
     * storage. Shared by {@link create} and {@link createForSiteWidgets}.
     */
    static registerCoreServices(params) {
        container.register(GlobalConfig, { useValue: params.config });
        container.register(RestaumaticRPCService, {
            useValue: new RestaumaticRPCService(params.rpcUrl),
        });
        container.register(LocalizationProvider, {
            useValue: new LocalizationProvider(params.config),
        });
        container.register(IStorage, { useValue: params.storage });
    }
    static create(params) {
        var _a;
        RestaumaticClient_1.registerCoreServices(params);
        container.register(IQrWaiter, { useValue: params.qrWaiter });
        container.register(ApplePayServiceProvider, {
            useValue: ApplePayServiceProvider.create({
                isApplePaySupported: params.config.isApplePaySupported,
                mobileAppType: params.config.mobileAppType,
                currency: params.config.currency,
            }),
        });
        const isQrWaiterOrder = params.qrWaiter.isQrWaiterOrder();
        const salesChannel = (_a = params.salesChannel) !== null && _a !== void 0 ? _a : SalesChannel.create(isQrWaiterOrder, params.config.mobileAppType);
        let menuConsumer;
        switch (salesChannel.value) {
            case "VoiceOrder":
                menuConsumer = "VoiceOrder";
                break;
            case "QrWaiter":
                menuConsumer = "QrWaiter";
                break;
            case "IosApp":
            case "AndroidApp":
                menuConsumer = "MobileApp";
                break;
            default:
                menuConsumer = "Site";
        }
        container.register(MenuConsumer, { useValue: menuConsumer });
        container.register(SalesChannel, { useValue: salesChannel });
        container.register(StreetAddress, {
            useValue: container.resolve(StreetAddressFactory).create(),
        });
        container.register(ApartmentInfoToken, {
            useValue: container.resolve(ApartmentInfoFactory).create(),
        });
        if (__ENABLE_DEBUG__) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const debugModule = require("../debug");
            debugModule.registerDebugFunctions();
        }
        return container.resolve(RestaumaticClient_1);
    }
    /**
     * Lightweight variant of {@link create} for standalone Sites widgets
     * (marketing-consent withdrawal, delivery checker, table reservation) that
     * render outside the ordering funnel. Registers only the shared primitives —
     * config, RPC, i18n and storage — without the per-restaurant/menu setup.
     */
    static createForSiteWidgets(params) {
        RestaumaticClient_1.registerCoreServices(params);
        return container.resolve(RestaumaticClient_1);
    }
    createMarketingConsentWithdrawal(accountId) {
        return MarketingConsentWithdrawalController.create({
            accountId,
            withdrawalService: container.resolve(MarketingConsentWithdrawalService),
            storageManager: this.storageManagerFactory.forMarketingConsentWithdrawal(),
        });
    }
    async recordCartSnapshotVisit(token, visitId) {
        const result = await container
            .resolve(RestaumaticRPCService)
            .rpc("RecordCartSnapshotVisit", { token, visitId });
        return result !== null;
    }
    async createRestaurantContainer(params) {
        const backendData = await container
            .resolve(BackendDataService)
            .getBackendData({
            restaurantId: params.restaurantId,
            menuResponse: params.menuResponse,
            fetchPaymentChannels: params.fetchPaymentChannels,
        });
        const restaurantContainer = await container
            .resolve(PerRestaurantContainerFactory)
            .create(backendData);
        if (params.placeOrderCallback) {
            restaurantContainer.register(PlaceOrderCallback, {
                useValue: params.placeOrderCallback,
            });
        }
        return restaurantContainer;
    }
    async createMobileApps(menuResponse, placeOrderCallback) {
        const restaurantContainer = await this.createRestaurantContainer({
            restaurantId: menuResponse.id,
            menuResponse: Promise.resolve(menuResponse),
            fetchPaymentChannels: true,
            placeOrderCallback,
        });
        return restaurantContainer.resolve(PMobileApps);
    }
    async createVoiceOrderingApp(params) {
        const restaurantContainer = await this.createRestaurantContainer({
            restaurantId: params.menuResponse.id,
            menuResponse: Promise.resolve(params.menuResponse),
            // Voice ordering doesn't need payment channels initially
            fetchPaymentChannels: false,
            placeOrderCallback: params.placeOrderCallback,
        });
        return [restaurantContainer, restaurantContainer.resolve(PRestaurant)];
    }
    createOrderSummary(params) {
        var _a;
        const orderSummaryFactory = container.resolve(POrderSummaryFactory);
        return orderSummaryFactory.create({
            ...params,
            placeOrderCallback: (_a = params.placeOrderCallback) !== null && _a !== void 0 ? _a : null,
        });
    }
    async createCheckoutSites(params) {
        const restaurantContainer = await this.createRestaurantContainer({
            restaurantId: params.restaurantId,
            menuResponse: container.resolve(MenuService).getMenu(params.menuPath),
            fetchPaymentChannels: true,
            placeOrderCallback: params.placeOrderCallback,
        });
        return restaurantContainer.resolve(PCheckout);
    }
    async createCartSites(params) {
        const restaurantContainer = await this.createRestaurantContainer({
            restaurantId: params.restaurantId,
            menuResponse: container.resolve(MenuService).getMenu(params.menuPath),
            fetchPaymentChannels: false,
        });
        return [
            restaurantContainer.resolve(PCartIntegration),
            Promise.resolve(restaurantContainer.resolve(PCart)),
        ];
    }
    createRestaurantSelection(initialRestaurants) {
        const restaurantSelectionFactory = container.resolve(PRestaurantSelectionFactory);
        return restaurantSelectionFactory.create(initialRestaurants);
    }
};
RestaumaticClient = RestaumaticClient_1 = __decorate([
    singleton(),
    __metadata("design:paramtypes", [typeof (_a = typeof StorageManagerFactory !== "undefined" && StorageManagerFactory) === "function" ? _a : Object])
], RestaumaticClient);
export { RestaumaticClient };
